import { Controller } from '@hotwired/stimulus';
import { GoogleCharts } from 'google-charts';

export default class extends Controller {
  static targets = ['pie', 'circle', 'column', 'gauge', 'radar'];

  static values = {
    locale: String,
    legend: {
      type: Boolean,
      default: true,
    },
  };

  connect() {
    GoogleCharts.load(this.drawCharts.bind(this));
    GoogleCharts.load('upcoming', {
      packages: ['gauge', 'vegachart'],
    });
  }

  targetsVisible() {
    return (
      (!this.hasPieTarget || this.pieTarget.offsetParent !== null) &&
      (!this.hasCircleTarget || this.circleTarget.offsetParent !== null) &&
      (!this.hasColumnTarget || this.columnTarget.offsetParent !== null) &&
      (!this.hasGaugeTarget || this.gaugeTarget.offsetParent !== null) &&
      (!this.hasRadarTarget || this.radarTarget.offsetParent !== null)
    );
  }

  drawCharts() {
    // Target elements are placed on a tab which may not be selected yet.
    // Wait until both targets are visible
    const checkExist = setInterval(() => {
      if (this.targetsVisible()) {
        if (this.hasPieTarget) this.drawPieChart();

        if (this.hasCircleTarget) this.drawCircleChart();

        if (this.hasColumnTarget) this.drawColumnChart();

        if (this.hasGaugeTarget) this.drawGaugeChart();

        if (this.hasRadarTarget) this.drawRadarChart();

        clearInterval(checkExist);
      }
    }, 100);
  }

  drawRadarChart() {
    const data = JSON.parse(this.radarTarget.dataset.values);

    const color = '#B82E2E';

    const dataTable = new GoogleCharts.api.visualization.DataTable();
    dataTable.addColumn({ type: 'string', id: 'key' });
    dataTable.addColumn({ type: 'number', id: 'value' });
    dataTable.addColumn({ type: 'string', id: 'pattern' });
    dataTable.addColumn({ type: 'string', id: 'color' });
    dataTable.addRows(data);

    const radarOptions = {
      vega: {
        $schema: 'https://vega.github.io/schema/vega/v5.json',
        width: 540,
        height: 600,
        autosize: 'none',
        signals: [{ name: 'radius', update: '220' }],
        data: [
          {
            name: 'table',
            source: 'datatable',
          },
        ],
        scales: [
          {
            name: 'angular',
            type: 'point',
            range: { signal: '[-PI, PI]' },
            padding: 0.5,
            domain: { data: 'table', field: 'key' },
          },
          {
            name: 'radial',
            type: 'linear',
            range: { signal: '[0, radius]' },
            zero: true,
            nice: false,
            domain: [0, 1],
          },
        ],
        encode: {
          enter: {
            x: { signal: 'width/2' },
            y: { signal: 'height/2 + 20' },
          },
        },
        marks: [
          {
            type: 'group',
            name: 'patterns',
            zindex: 1,
            from: {
              data: 'table',
            },
            marks: [
              {
                type: 'line',
                name: 'pattern-line',
                from: { data: 'table' },
                encode: {
                  enter: {
                    interpolate: { value: 'linear-closed' },
                    x: {
                      signal:
                        "scale('radial', datum.value) * cos(scale('angular', datum.key))",
                    },
                    y: {
                      signal:
                        "scale('radial', datum.value) * sin(scale('angular', datum.key))",
                    },
                    stroke: { value: color },
                    strokeWidth: { value: 1.5 },
                    fill: { value: color },
                    fillOpacity: { value: 0.1 },
                  },
                },
              },
              {
                type: 'text',
                name: 'value-text',
                from: { data: 'pattern-line' },
                encode: {
                  enter: {
                    x: {
                      signal:
                        "datum.x + 14 * cos(scale('angular', datum.datum.key))",
                    },
                    y: {
                      signal:
                        "datum.y + 14 * sin(scale('angular', datum.datum.key))",
                    },
                    text: {
                      signal: "format(datum.datum.value,'.0%')",
                    },
                    opacity: {
                      signal: 'datum.datum.value > 0.01 ? 1 : 0',
                    },
                    align: { value: 'center' },
                    baseline: { value: 'middle' },
                    fontWeight: { value: 'bold' },
                    fill: { value: color },
                  },
                },
              },
            ],
          },
          {
            type: 'rule',
            name: 'radial-grid',
            from: { data: 'table' },
            zindex: 0,
            encode: {
              enter: {
                x: { value: 0 },
                y: { value: 0 },
                x2: {
                  signal: "radius * cos(scale('angular', datum.key))",
                },
                y2: {
                  signal: "radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'text',
            name: 'key-label',
            from: { data: 'table' },
            zindex: 1,
            encode: {
              enter: {
                x: {
                  signal: "(radius + 25) * cos(scale('angular', datum.key))",
                },
                y: [
                  {
                    test: "sin(scale('angular', datum.key)) > 0",
                    signal: "(radius + 25) * sin(scale('angular', datum.key))",
                  },
                  {
                    test: "sin(scale('angular', datum.key)) < 0",
                    signal: "(radius + 25) * sin(scale('angular', datum.key))",
                  },
                  {
                    signal: "(radius + 20) * sin(scale('angular', datum.key))",
                  },
                ],
                text: { field: 'key' },
                align: {
                  value: 'center',
                },
                baseline: {
                  value: 'middle',
                },
                fill: { value: 'black' },
                fontSize: { value: 12 },
              },
            },
          },
          {
            type: 'text',
            name: 'key-element',
            from: { data: 'table' },
            zindex: 1,
            encode: {
              enter: {
                x: {
                  signal: "(radius + 20) * cos(scale('angular', datum.key))",
                },
                y: [
                  {
                    test: "sin(scale('angular', datum.key)) > 0",
                    signal: "(radius + 50) * sin(scale('angular', datum.key))",
                  },
                  {
                    test: "sin(scale('angular', datum.key)) < 0",
                    signal: "(radius + 50) * sin(scale('angular', datum.key))",
                  },
                  {
                    signal:
                      "20 + (radius + 50) * sin(scale('angular', datum.key))",
                  },
                ],
                text: { field: 'pattern' },
                align: {
                  value: 'center',
                },
                baseline: {
                  value: 'middle',
                },
                fill: { field: 'color' },
                fontSize: { value: 11 },
              },
            },
          },
          {
            type: 'line',
            name: 'twenty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.2 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.2 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'fourty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.4 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.4 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'sixty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.6 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.6 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'eighty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.8 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.8 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'outer-line',
            from: { data: 'radial-grid' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: { field: 'x2' },
                y: { field: 'y2' },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
        ],
      },
    };

    const radarChart = new GoogleCharts.api.visualization.VegaChart(
      this.radarTarget,
    );
    radarChart.draw(dataTable, radarOptions);
  }

  drawGaugeChart() {
    const data = JSON.parse(this.gaugeTarget.dataset.values);

    const gaugeData = GoogleCharts.api.visualization.arrayToDataTable(data);
    const gaugeOptions = {
      min: 0,
      max: 6,
      majorTicks: ['0', '1', '2', '3', '4', '5', '6'],
      minorTicks: 2,
      greenFrom: 0,
      greenTo: 0.5,
      greenColor: 'transparent',
      yellowFrom: 0.5,
      yellowTo: 2,
      yellowColor: 'transparent',
      redFrom: 2,
      redTo: 6,
      redColor: 'transparent',
      height: 210,
    };

    const gaugeChart = new GoogleCharts.api.visualization.Gauge(
      this.gaugeTarget,
    );

    if (!this.onPrint)
      GoogleCharts.api.visualization.events.addListener(
        gaugeChart,
        'ready',
        () => {
          const yellow = '#ebe051';
          const black = '#0e1523';
          const white = '#ffffff';
          const gray = '#555555';

          // Background
          this.gaugeTarget.querySelector('g circle:nth-child(2)').style.fill =
            black;

          // Ticks
          this.gaugeTarget.querySelectorAll('path').forEach((path) => {
            path.style.stroke = white;
          });

          // Numbers
          this.gaugeTarget.querySelectorAll('g text').forEach((text) => {
            text.style.fill = yellow;
          });

          // Label
          this.gaugeTarget.querySelector('g text').style.fill = 'white';
          this.gaugeTarget.querySelector('g text').style.fontSize = 19;
          this.gaugeTarget.querySelector('g text').style.fontWeight = 300;
          this.gaugeTarget.querySelector('g text').style.fontFamily = 'Poppins';

          // Hide the current value text
          this.gaugeTarget.querySelectorAll('g g text').forEach((text) => {
            text.style.fill = 'transparent';
          });

          // Needle
          this.gaugeTarget.querySelectorAll('g g path').forEach((path) => {
            path.style.fill = yellow;
            path.style.stroke = yellow;
          });

          // Big inner circle
          this.gaugeTarget.querySelectorAll('g g circle').forEach((circle) => {
            circle.style.fill = black;
            circle.style.stroke = gray;
          });
        },
      );

    gaugeChart.draw(gaugeData, gaugeOptions);
  }

  drawPieChart() {
    const data = JSON.parse(this.pieTarget.dataset.values);
    data.unshift(['Element', 'Kraft']);
    const colors = JSON.parse(this.pieTarget.dataset.colors);

    const pieData = GoogleCharts.api.visualization.arrayToDataTable(data);
    const pieOptions = {
      colors,
      legend: {
        position:
          this.pieTarget.clientHeight < 200 || !this.legendValue
            ? 'none'
            : 'bottom',
        alignment: 'center',
        textStyle: { color: '#bbb' },
      },
      chartArea: {
        top: this.pieTarget.clientHeight < 200 ? 0 : 10,
        right: 0,
        bottom: this.pieTarget.clientHeight < 200 ? 0 : 50,
        left: 0,
      },
      width: this.pieTarget.clientWidth,
      height: this.pieTarget.clientHeight,
      backgroundColor: 'transparent',
      pieSliceText: 'none',
    };

    const pieChart = new GoogleCharts.api.visualization.PieChart(
      this.pieTarget,
    );
    pieChart.draw(pieData, pieOptions);
  }

  drawCircleChart() {
    const data = JSON.parse(this.circleTarget.dataset.values);
    console.log(data);
    data.unshift(['Element', 'Kraft']);

    let desiredOrder = [];
    if (this.localeValue == 'en') {
      desiredOrder = ['Element', 'Fire', 'Earth', 'Metal', 'Water', 'Wood'];
    } else {
      desiredOrder = ['Element', 'Feuer', 'Erde', 'Metall', 'Wasser', 'Holz'];
    }

    const defaultData = desiredOrder.map((element) => {
      return element === 'Element' ? [element, 'Kraft'] : [element, 0];
    });

    const mergedData = defaultData.map((defaultRow) => {
      const matchingRow = data.find((row) => row[0] === defaultRow[0]);
      return matchingRow || defaultRow;
    });

    const pieData = GoogleCharts.api.visualization.arrayToDataTable(mergedData);

    const formatter = new GoogleCharts.api.visualization.NumberFormat({
      suffix: '%',
      pattern: '#',
    });
    formatter.format(pieData, 1);

    const pieOptions = {
      pieHole: 0.9,
      legend: {
        labeledValueText: 'value',
        position: 'labeled',
        alignment: 'center',
      },
      chartArea: {
        bottom: 10,
      },
      enableInteractivity: false,
      colors: ['#F74F4F', '#DB7704', '#DEDEDE', '#179DEB', '#60DB71'],
      backgroundColor: 'transparent',
      pieSliceText: 'none',
      sliceVisibilityThreshold: 0,
    };
    const pieChart = new GoogleCharts.api.visualization.PieChart(
      this.circleTarget,
    );
    pieChart.draw(pieData, pieOptions);
  }

  drawColumnChart() {
    const data = JSON.parse(this.columnTarget.dataset.values);
    const colors = JSON.parse(this.columnTarget.dataset.colors);
    const dataCompare = this.columnTarget.dataset.valuesCompare
      ? JSON.parse(this.columnTarget.dataset.valuesCompare)
      : null;

    const colData = new GoogleCharts.api.visualization.DataTable();
    colData.addColumn('string', '');

    if (dataCompare) {
      colData.addColumn('number', '');
      colData.addColumn({ type: 'string', role: 'style' });
      colData.addColumn({ type: 'boolean', role: 'certainty' }); // Hack to render striped fill
    }

    colData.addColumn('number', '');
    colData.addColumn({ type: 'string', role: 'style' });
    colData.addColumn({ type: 'boolean', role: 'certainty' }); // Hack to render striped fill

    colData.addRows(data.length);
    for (let i = 0; i < data.length; i++) {
      if (dataCompare) {
        colData.setValue(i, 0, data[i][0]);

        // 1st
        colData.setValue(i, 1, dataCompare[i] ? dataCompare[i][1] : 0);
        colData.setValue(i, 2, colors[i]);
        colData.setValue(i, 3, false);

        // 2nd
        colData.setValue(i, 4, data[i][1]);
        colData.setValue(i, 5, colors[i]);
        colData.setValue(i, 6, true);
      } else {
        colData.setValue(i, 0, data[i][0]);
        colData.setValue(i, 1, data[i][1]);
        colData.setValue(i, 2, colors[i]);
        colData.setValue(i, 3, true);
      }
    }

    const colOptions = {
      backgroundColor: 'transparent',
      legend: { position: 'none' },
      hAxis: {
        textStyle: {
          fontSize: 11,
          color: '#bbb',
        },
      },
      vAxis: {
        gridlines: { count: 4 },
        viewWindow: {
          min: 0,
          max: 6,
        },
        baselineColor: '#bbb',
        textStyle: {
          color: '#bbb',
        },
      },
    };

    const colChart = new GoogleCharts.api.visualization.ColumnChart(
      this.columnTarget,
    );
    colChart.draw(colData, colOptions);
  }

  get onPrint() {
    return document
      .getElementsByTagName('body')[0]
      .classList.contains('js-print');
  }
}
